import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ==================== EXAM MODE SELECTOR ====================
export const ExamModeSelector = ({ onSelectMode, userName }) => {
  const modes = [
    {
      id: 'diagnostic',
      name: 'מבחן אבחוני',
      icon: '🔬',
      description: '100 שאלות מעורבות לזיהוי נקודות חולשה',
      color: 'from-blue-500 to-cyan-600',
      time: '3 שעות',
      features: ['זיהוי נקודות חולשה', 'דוח מפורט', 'המלצות אישיות']
    },
    {
      id: 'adaptive',
      name: 'תרגול אדפטיבי',
      icon: '🧠',
      description: 'שאלות מותאמות אישית לרמה שלך',
      color: 'from-purple-500 to-pink-600',
      time: 'ללא הגבלה',
      features: ['התאמה לרמה', 'למידה מטעויות', 'שיפור מתמיד']
    },
    {
      id: 'simulator',
      name: 'סימולטור מלא',
      icon: '📝',
      description: 'הדמיית בחינת לשכה אמיתית',
      color: 'from-amber-500 to-orange-600',
      time: '3 שעות',
      features: ['100 שאלות', 'טיימר אמיתי', 'תנאי בחינה']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6" dir="rtl">
      {/* Skip link */}
      <a href="#mode-selection" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:right-4 bg-purple-600 text-white px-4 py-2 rounded-lg z-50">
        דלג לבחירת מצב
      </a>

      <div className="max-w-5xl mx-auto">
        <header className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl mb-6 shadow-2xl shadow-purple-500/30">
            <span className="text-4xl" aria-hidden="true">⚖️</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-white mb-4">
            בחינת לשכת עורכי הדין
          </h1>
          <p className="text-slate-400 text-lg">
            שלום <span className="text-purple-400 font-medium">{userName}</span>, בחר את מצב הלימוד שלך
          </p>
        </header>

        <main id="mode-selection" role="main">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6" role="list" aria-label="מצבי לימוד">
            {modes.map((mode) => (
              <button
                key={mode.id}
                onClick={() => onSelectMode(mode.id)}
                className={`bg-gradient-to-br ${mode.color} p-6 sm:p-8 rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] text-white text-right group focus:outline-none focus:ring-4 focus:ring-white/30`}
                data-testid={`mode-${mode.id}`}
                role="listitem"
                aria-label={`${mode.name}: ${mode.description}. זמן: ${mode.time}`}
              >
                <div className="text-5xl sm:text-6xl mb-4 group-hover:scale-110 transition-transform" aria-hidden="true">
                  {mode.icon}
                </div>
                <h2 className="text-xl sm:text-2xl font-black mb-2">{mode.name}</h2>
                <p className="text-white/80 text-sm mb-4">{mode.description}</p>
                
                {/* Features list */}
                <ul className="space-y-1 mb-4" aria-label="תכונות">
                  {mode.features.map((feature, i) => (
                    <li key={i} className="text-xs text-white/70 flex items-center gap-2">
                      <span aria-hidden="true">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <div className="flex items-center gap-2 text-sm bg-white/20 rounded-full px-4 py-2 w-fit">
                  <span aria-hidden="true">⏱️</span>
                  <span>{mode.time}</span>
                </div>
              </button>
            ))}
          </div>
        </main>

        {/* Keyboard hint */}
        <footer className="text-center mt-8 text-slate-500 text-sm">
          <p>לחץ <kbd className="px-2 py-1 bg-slate-700 rounded text-slate-300">Tab</kbd> לניווט בין האפשרויות</p>
        </footer>
      </div>
    </div>
  );
};

// ==================== ADAPTIVE EXAM VIEW ====================
export const AdaptiveExamView = ({ userId, mode, onComplete, onBack }) => {
  const [session, setSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answeredIds, setAnsweredIds] = useState([]);
  const [timeLeft, setTimeLeft] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [stats, setStats] = useState({ correct: 0, total: 0, streak: 0 });

  // Start session
  useEffect(() => {
    const startSession = async () => {
      try {
        const response = await axios.post(`${API}/adaptive/session/start`, {
          user_id: userId,
          mode: mode
        });
        setSession(response.data);
        
        if (response.data.time_limit > 0) {
          setTimeLeft(response.data.time_limit);
        }
        
        if (response.data.first_question) {
          setCurrentQuestion(response.data.first_question);
        } else {
          await fetchNextQuestion(response.data.session_id);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Failed to start session:', error);
        setLoading(false);
      }
    };
    
    startSession();
  }, [userId, mode]);

  // Timer
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleComplete();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [timeLeft]);

  // Track question start time
  useEffect(() => {
    if (currentQuestion) {
      setStartTime(Date.now());
    }
  }, [currentQuestion]);

  const fetchNextQuestion = async (sessionId) => {
    try {
      if (mode === 'adaptive') {
        const response = await axios.post(`${API}/adaptive/question/next`, {
          user_id: userId,
          session_id: sessionId,
          exclude_ids: answeredIds
        });
        setCurrentQuestion(response.data);
      } else {
        // For diagnostic/simulator, get from pre-loaded questions
        const sessionData = await axios.get(`${API}/adaptive/session/${sessionId}`);
        const questions = sessionData.data.questions || [];
        if (questionIndex < questions.length) {
          setCurrentQuestion(questions[questionIndex]);
        } else {
          handleComplete();
        }
      }
    } catch (error) {
      console.error('Failed to fetch question:', error);
      handleComplete();
    }
  };

  const handleAnswerSelect = (index) => {
    if (showResult) return;
    setSelectedAnswer(index);
  };

  const handleSubmitAnswer = async () => {
    if (selectedAnswer === null || !session || !currentQuestion) return;
    
    const timeTaken = (Date.now() - startTime) / 1000;
    
    try {
      const response = await axios.post(`${API}/adaptive/answer/submit`, {
        session_id: session.session_id,
        question_id: currentQuestion.id,
        selected_answer: selectedAnswer,
        time_taken: timeTaken
      });
      
      setResult(response.data);
      setShowResult(true);
      setAnsweredIds(prev => [...prev, currentQuestion.id]);
      
      setStats(prev => ({
        correct: prev.correct + (response.data.is_correct ? 1 : 0),
        total: prev.total + 1,
        streak: response.data.current_streak
      }));
      
    } catch (error) {
      console.error('Failed to submit answer:', error);
    }
  };

  const handleNextQuestion = async () => {
    setShowResult(false);
    setSelectedAnswer(null);
    setResult(null);
    setQuestionIndex(prev => prev + 1);
    
    if (mode === 'adaptive') {
      await fetchNextQuestion(session.session_id);
    } else {
      const sessionData = await axios.get(`${API}/adaptive/session/${session.session_id}`);
      const questions = sessionData.data.questions || [];
      if (questionIndex + 1 < questions.length) {
        setCurrentQuestion(questions[questionIndex + 1]);
      } else {
        handleComplete();
      }
    }
  };

  const handleComplete = async () => {
    if (!session) return;
    
    try {
      const response = await axios.post(`${API}/adaptive/session/${session.session_id}/complete`);
      onComplete(response.data);
    } catch (error) {
      console.error('Failed to complete session:', error);
      onComplete({ error: true });
    }
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin text-6xl mb-4">⚖️</div>
          <p className="text-white text-xl">מכין את המבחן...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" dir="rtl">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur-lg border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={onBack}
            className="text-slate-400 hover:text-white transition-colors"
          >
            ← חזרה
          </button>
          
          <div className="flex items-center gap-6">
            {/* Stats */}
            <div className="flex items-center gap-4 text-sm">
              <span className="text-green-400">✓ {stats.correct}</span>
              <span className="text-slate-400">/ {stats.total}</span>
              {stats.streak > 0 && (
                <span className="bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded-full text-xs">
                  🔥 {stats.streak}
                </span>
              )}
            </div>
            
            {/* Timer */}
            {timeLeft !== null && (
              <div className={`font-mono text-lg ${timeLeft < 300 ? 'text-red-400' : 'text-white'}`}>
                ⏱️ {formatTime(timeLeft)}
              </div>
            )}
          </div>
          
          <button
            onClick={handleComplete}
            className="bg-red-500/20 text-red-400 px-4 py-2 rounded-lg hover:bg-red-500/30 transition-colors"
          >
            סיום
          </button>
        </div>
      </div>

      {/* Question */}
      <main className="max-w-4xl mx-auto p-4 sm:p-6" id="main-content">
        {currentQuestion && (
          <article 
            className="bg-slate-800/50 backdrop-blur-lg rounded-3xl p-6 sm:p-8 shadow-2xl border border-slate-700/50"
            aria-labelledby="question-text"
          >
            {/* Question Header */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
              <span 
                className="bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full text-sm font-medium"
                aria-label={`נושא: ${getSubjectHebrew(currentQuestion.subject)}`}
              >
                {getSubjectHebrew(currentQuestion.subject)}
              </span>
              <div className="flex items-center gap-3">
                {currentQuestion.difficulty && (
                  <span className="text-xs text-slate-400" aria-label={`רמת קושי: ${currentQuestion.difficulty}`}>
                    רמה {currentQuestion.difficulty}/4
                  </span>
                )}
                <span className="text-slate-500 text-sm">
                  שאלה {questionIndex + 1}
                </span>
              </div>
            </div>

            {/* Question Text */}
            <h2 
              id="question-text"
              className="text-lg sm:text-xl text-white font-medium leading-relaxed mb-8"
            >
              {currentQuestion.text}
            </h2>

            {/* Options */}
            <fieldset className="space-y-3 sm:space-y-4" role="radiogroup" aria-label="אפשרויות תשובה">
              <legend className="sr-only">בחר תשובה</legend>
              {currentQuestion.options.map((option, index) => {
                let optionClass = 'bg-slate-700/50 border-slate-600 hover:border-purple-500 hover:bg-slate-700/70';
                let iconClass = 'bg-slate-600 text-slate-300';
                
                if (showResult) {
                  if (index === result.correct_answer) {
                    optionClass = 'bg-green-500/20 border-green-500';
                    iconClass = 'bg-green-500 text-white';
                  } else if (index === selectedAnswer && !result.is_correct) {
                    optionClass = 'bg-red-500/20 border-red-500';
                    iconClass = 'bg-red-500 text-white';
                  }
                } else if (selectedAnswer === index) {
                  optionClass = 'bg-purple-500/20 border-purple-500';
                  iconClass = 'bg-purple-500 text-white';
                }
                
                return (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(index)}
                    disabled={showResult}
                    className={`w-full p-4 rounded-xl border-2 text-right text-white transition-all focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-900 ${optionClass}`}
                    data-testid={`option-${index}`}
                    role="radio"
                    aria-checked={selectedAnswer === index}
                    aria-label={`תשובה ${['א', 'ב', 'ג', 'ד'][index]}: ${option}`}
                  >
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-center font-bold ml-3 transition-colors ${iconClass}`} aria-hidden="true">
                      {['א', 'ב', 'ג', 'ד'][index]}
                    </span>
                    <span className="text-sm sm:text-base">{option}</span>
                  </button>
                );
              })}
            </fieldset>

            {/* Result & Explanation */}
            {showResult && result && (
              <div 
                className={`mt-8 p-5 sm:p-6 rounded-xl ${result.is_correct ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}
                role="alert"
                aria-live="polite"
              >
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-3xl" aria-hidden="true">{result.is_correct ? '✅' : '❌'}</span>
                  <span className={`text-lg sm:text-xl font-bold ${result.is_correct ? 'text-green-400' : 'text-red-400'}`}>
                    {result.is_correct ? 'תשובה נכונה!' : 'תשובה שגויה'}
                  </span>
                  {result.level_change && (
                    <span 
                      className={`px-3 py-1 rounded-full text-sm font-medium ${result.level_change === 'up' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}
                      aria-label={result.level_change === 'up' ? 'עלית רמה' : 'ירדת רמה'}
                    >
                      {result.level_change === 'up' ? '⬆️ עלית רמה!' : '⬇️ ירדת רמה'}
                    </span>
                  )}
                </div>
                <p className="text-slate-300 leading-relaxed text-sm sm:text-base">
                  {result.explanation}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="mt-8 flex justify-center">
              {!showResult ? (
                <button
                  onClick={handleSubmitAnswer}
                  disabled={selectedAnswer === null}
                  className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-8 sm:px-12 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-purple-500/30 transition-all focus:outline-none focus:ring-4 focus:ring-purple-500/50"
                  data-testid="submit-answer"
                  aria-disabled={selectedAnswer === null}
                >
                  שלח תשובה
                </button>
              ) : (
                <button
                  onClick={handleNextQuestion}
                  className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-8 sm:px-12 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg hover:shadow-lg hover:shadow-green-500/30 transition-all focus:outline-none focus:ring-4 focus:ring-green-500/50"
                  data-testid="next-question"
                >
                  שאלה הבאה →
                </button>
              )}
            </div>
          </article>
        )}
      </main>
    </div>
  );
};

// ==================== RESULTS VIEW ====================
export const ExamResultsView = ({ results, onBack, onRetry }) => {
  const getGrade = (score) => {
    if (score >= 90) return { text: 'מצוין', color: 'text-green-400', emoji: '🏆' };
    if (score >= 80) return { text: 'טוב מאוד', color: 'text-green-400', emoji: '⭐' };
    if (score >= 70) return { text: 'טוב', color: 'text-yellow-400', emoji: '👍' };
    if (score >= 60) return { text: 'עובר', color: 'text-yellow-400', emoji: '✓' };
    return { text: 'נכשל', color: 'text-red-400', emoji: '📚' };
  };

  const grade = getGrade(results.final_score || 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6" dir="rtl">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="text-8xl mb-4">{grade.emoji}</div>
          <h1 className={`text-5xl font-black ${grade.color} mb-2`}>
            {results.final_score?.toFixed(1)}%
          </h1>
          <p className={`text-2xl ${grade.color}`}>{grade.text}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-800/50 p-6 rounded-2xl text-center">
            <div className="text-3xl font-bold text-white">{results.correct_answers}</div>
            <div className="text-slate-400 text-sm">תשובות נכונות</div>
          </div>
          <div className="bg-slate-800/50 p-6 rounded-2xl text-center">
            <div className="text-3xl font-bold text-white">{results.total_questions}</div>
            <div className="text-slate-400 text-sm">סה"כ שאלות</div>
          </div>
          <div className="bg-slate-800/50 p-6 rounded-2xl text-center">
            <div className={`text-3xl font-bold ${results.readiness_score >= 82 ? 'text-green-400' : 'text-yellow-400'}`}>
              {results.readiness_score?.toFixed(1)}
            </div>
            <div className="text-slate-400 text-sm">ציון מוכנות</div>
          </div>
          <div className="bg-slate-800/50 p-6 rounded-2xl text-center">
            <div className={`text-3xl font-bold ${results.pass_probability >= 60 ? 'text-green-400' : 'text-red-400'}`}>
              {results.pass_probability?.toFixed(0)}%
            </div>
            <div className="text-slate-400 text-sm">סיכוי מעבר</div>
          </div>
        </div>

        {/* Subject Breakdown */}
        {results.subject_breakdown && Object.keys(results.subject_breakdown).length > 0 && (
          <div className="bg-slate-800/50 rounded-2xl p-6 mb-8">
            <h3 className="text-xl font-bold text-white mb-4">פירוט לפי נושא</h3>
            <div className="space-y-3">
              {Object.entries(results.subject_breakdown).map(([subject, data]) => (
                <div key={subject} className="flex items-center gap-4">
                  <div className="w-32 text-slate-400 text-sm truncate">{subject}</div>
                  <div className="flex-1 bg-slate-700 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${data.accuracy >= 70 ? 'bg-green-500' : data.accuracy >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                      style={{ width: `${data.accuracy}%` }}
                    />
                  </div>
                  <div className="w-16 text-left text-white font-medium">
                    {data.accuracy}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Readiness Indicator */}
        <div className={`p-6 rounded-2xl mb-8 ${results.is_ready ? 'bg-green-500/10 border border-green-500/30' : 'bg-yellow-500/10 border border-yellow-500/30'}`}>
          <div className="flex items-center gap-4">
            <span className="text-4xl">{results.is_ready ? '✅' : '⚠️'}</span>
            <div>
              <h3 className={`text-xl font-bold ${results.is_ready ? 'text-green-400' : 'text-yellow-400'}`}>
                {results.is_ready ? 'מוכן לבחינה!' : 'צריך עוד תרגול'}
              </h3>
              <p className="text-slate-400">
                {results.is_ready 
                  ? 'הביצועים שלך מצביעים על מוכנות טובה לבחינת הלשכה' 
                  : 'המשך לתרגל כדי לשפר את הציון שלך'}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-center gap-4">
          <button
            onClick={onBack}
            className="bg-slate-700 text-white px-8 py-4 rounded-xl font-bold hover:bg-slate-600 transition-colors"
          >
            חזרה לתפריט
          </button>
          <button
            onClick={onRetry}
            className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-8 py-4 rounded-xl font-bold hover:shadow-lg transition-all"
          >
            תרגול נוסף
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== DASHBOARD VIEW ====================
export const UserDashboard = ({ userId, onStartExam, onBack }) => {
  const [performance, setPerformance] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [perfRes, readyRes] = await Promise.all([
          axios.get(`${API}/adaptive/performance/${userId}`),
          axios.get(`${API}/adaptive/readiness/${userId}`)
        ]);
        setPerformance(perfRes.data);
        setReadiness(readyRes.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      }
      setLoading(false);
    };
    
    fetchData();
  }, [userId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin text-6xl">📊</div>
      </div>
    );
  }

  const hasData = performance && performance.total_questions_answered > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6" dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button onClick={onBack} className="text-slate-400 hover:text-white">
            ← חזרה
          </button>
          <h1 className="text-3xl font-black text-white">📊 לוח הבקרה שלי</h1>
          <div></div>
        </div>

        {!hasData ? (
          /* Empty State */
          <div className="text-center py-20">
            <div className="text-8xl mb-6">📚</div>
            <h2 className="text-2xl font-bold text-white mb-4">אין נתונים עדיין</h2>
            <p className="text-slate-400 mb-8">התחל לתרגל כדי לראות את הסטטיסטיקות שלך</p>
            <button
              onClick={() => onStartExam('adaptive')}
              className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-8 py-4 rounded-xl font-bold"
            >
              התחל תרגול אדפטיבי
            </button>
          </div>
        ) : (
          <>
            {/* Readiness Score */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="md:col-span-2 bg-slate-800/50 rounded-3xl p-8">
                <h3 className="text-slate-400 mb-2">ציון מוכנות</h3>
                <div className="flex items-end gap-4">
                  <span className={`text-7xl font-black ${readiness?.readiness_score >= 82 ? 'text-green-400' : readiness?.readiness_score >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                    {readiness?.readiness_score?.toFixed(0) || 0}
                  </span>
                  <span className="text-2xl text-slate-500 mb-2">/ 100</span>
                </div>
                <div className="mt-4 bg-slate-700 rounded-full h-4 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${readiness?.readiness_score >= 82 ? 'bg-green-500' : readiness?.readiness_score >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                    style={{ width: `${readiness?.readiness_score || 0}%` }}
                  />
                </div>
                <p className="text-slate-400 mt-4">
                  {readiness?.is_ready ? '✅ מוכן לבחינה!' : '⚠️ המשך לתרגל'}
                </p>
              </div>

              <div className="bg-slate-800/50 rounded-3xl p-8 text-center">
                <h3 className="text-slate-400 mb-4">סיכוי מעבר</h3>
                <div className={`text-6xl font-black ${readiness?.pass_probability >= 60 ? 'text-green-400' : 'text-red-400'}`}>
                  {readiness?.pass_probability?.toFixed(0) || 0}%
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-slate-800/50 p-6 rounded-2xl text-center">
                <div className="text-3xl font-bold text-white">{performance.total_questions_answered}</div>
                <div className="text-slate-400 text-sm">שאלות נפתרו</div>
              </div>
              <div className="bg-slate-800/50 p-6 rounded-2xl text-center">
                <div className="text-3xl font-bold text-green-400">{performance.overall_accuracy?.toFixed(1)}%</div>
                <div className="text-slate-400 text-sm">דיוק כללי</div>
              </div>
              <div className="bg-slate-800/50 p-6 rounded-2xl text-center">
                <div className="text-3xl font-bold text-yellow-400">{performance.max_streak}</div>
                <div className="text-slate-400 text-sm">רצף שיא</div>
              </div>
              <div className="bg-slate-800/50 p-6 rounded-2xl text-center">
                <div className="text-3xl font-bold text-purple-400">{performance.current_level}</div>
                <div className="text-slate-400 text-sm">רמה נוכחית</div>
              </div>
            </div>

            {/* Subject Performance */}
            {performance.subjects && Object.keys(performance.subjects).length > 0 && (
              <div className="bg-slate-800/50 rounded-2xl p-6 mb-8">
                <h3 className="text-xl font-bold text-white mb-4">ביצועים לפי נושא</h3>
                <div className="space-y-3">
                  {Object.entries(performance.subjects)
                    .sort((a, b) => a[1].accuracy - b[1].accuracy)
                    .map(([subject, data]) => (
                      <div key={subject} className="flex items-center gap-4">
                        <div className="w-40 text-slate-400 text-sm truncate">{subject}</div>
                        <div className="flex-1 bg-slate-700 rounded-full h-3 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${data.accuracy >= 70 ? 'bg-green-500' : data.accuracy >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${data.accuracy}%` }}
                          />
                        </div>
                        <div className="w-20 text-left">
                          <span className="text-white font-medium">{data.accuracy?.toFixed(0)}%</span>
                          <span className="text-slate-500 text-xs mr-2">({data.total})</span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {readiness?.recommendations && readiness.recommendations.length > 0 && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-6 mb-8">
                <h3 className="text-xl font-bold text-yellow-400 mb-4">💡 המלצות</h3>
                <ul className="space-y-2">
                  {readiness.recommendations.map((rec, i) => (
                    <li key={i} className="text-slate-300 flex items-start gap-2">
                      <span className="text-yellow-400">•</span>
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action Button */}
            <div className="text-center">
              <button
                onClick={() => onStartExam('adaptive')}
                className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-12 py-4 rounded-xl font-bold text-lg hover:shadow-lg transition-all"
              >
                המשך לתרגל
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== HELPER FUNCTION ====================
const getSubjectHebrew = (name) => {
  const names = {
    'civil-procedure': 'סדר דין אזרחי',
    'contracts': 'דיני חוזים',
    'torts': 'דיני נזיקין',
    'property': 'דיני קניין',
    'evidence_law': 'דיני ראיות',
    'legal_ethics': 'אתיקה מקצועית',
    'administrative_law': 'משפט מנהלי',
    'criminal_law': 'דיני עונשין',
    'labor_law': 'דיני עבודה',
    'corporations_law': 'דיני חברות'
  };
  return names[name] || name;
};

export default { ExamModeSelector, AdaptiveExamView, ExamResultsView, UserDashboard };
